import { UserPlus, Phone, Mail, RefreshCw, CheckCircle, MessageSquare, StickyNote, Edit, Search } from "lucide-react";
import { useMemo, useState } from "react";

import { Card, CardContent, CardHeader, CardTitle } from "@/app/_module/components/ui/card";
import { Input } from "@/app/_module/components/ui/input";
import { leadAssignmentService } from "../services/service";

interface Activity {
  id: string;
  type: "lead_assigned" | "status_changed" | "call_made" | "email_sent" | "sms_sent" | "note_added" | "lead_updated";
  leadName: string;
  agentName: string;
  agentAgency?: string;
  timestamp: string;
  description: string;
  householdPrimaryName?: string;
  householdPrimaryId?: string;
}

interface RecentActivityProps {
  activities: Activity[];
}

const TYPE_CHIPS: { key: string; label: string }[] = [
  { key: "all", label: "All" },
  { key: "lead_assigned", label: "Assigned" },
  { key: "call_made", label: "Calls" },
  { key: "sms_sent", label: "Texts" },
  { key: "email_sent", label: "Emails" },
  { key: "status_changed", label: "Status" },
  { key: "note_added", label: "Notes" },
  { key: "lead_updated", label: "Edits" },
];

export const relativeTime = (dateString: string) => {
  const diffMs = Date.now() - new Date(dateString).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateString).toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

export const dayLabel = (dateString: string) => {
  const d = new Date(dateString);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (sameDay(d, today)) return "Today";
  if (sameDay(d, yesterday)) return "Yesterday";
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
};

export const RecentActivity = function ({ activities }: RecentActivityProps) {
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [visibleCount, setVisibleCount] = useState(30);
  const [olderItems, setOlderItems] = useState<Activity[]>([]);
  const [nextBefore, setNextBefore] = useState<string | null | undefined>(undefined); // undefined = untried
  const [loadingOlder, setLoadingOlder] = useState(false);

  const getActivityIcon = (type: string) => {
    switch (type) {
      case "lead_assigned":
        return <UserPlus className="h-4 w-4 text-primary" />;
      case "status_changed":
        return <RefreshCw className="h-4 w-4 text-green-600" />;
      case "call_made":
        return <Phone className="h-4 w-4 text-purple-600" />;
      case "email_sent":
        return <Mail className="h-4 w-4 text-orange-600" />;
      case "sms_sent":
        return <MessageSquare className="h-4 w-4 text-cyan-600" />;
      case "note_added":
        return <StickyNote className="h-4 w-4 text-yellow-600" />;
      case "lead_updated":
        return <Edit className="h-4 w-4 text-indigo-600" />;
      default:
        return <CheckCircle className="h-4 w-4 text-gray-600" />;
    }
  };

  const getActivityColor = (type: string) => {
    switch (type) {
      case "lead_assigned":
        return "bg-primary/5 border-primary/30";
      case "status_changed":
        return "bg-green-50 border-green-200";
      case "call_made":
        return "bg-purple-50 border-purple-200";
      case "email_sent":
        return "bg-orange-50 border-orange-200";
      case "sms_sent":
        return "bg-cyan-50 border-cyan-200";
      case "note_added":
        return "bg-yellow-50 border-yellow-200";
      case "lead_updated":
        return "bg-indigo-50 border-indigo-200";
      default:
        return "bg-gray-50 border-gray-200";
    }
  };

  const formatTime = (dateString: string) =>
    new Date(dateString).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });

  // live snapshot + server-paged older history (deduped)
  const allItems = useMemo(() => {
    if (olderItems.length === 0) return activities;
    const seen = new Set(activities.map((a) => `${a.type}-${a.leadName}-${a.timestamp}`));
    return [...activities, ...olderItems.filter((o) => !seen.has(`${o.type}-${o.leadName}-${o.timestamp}`))];
  }, [activities, olderItems]);

  // counts per type for the chips
  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = { all: allItems.length };
    for (const a of allItems) counts[a.type] = (counts[a.type] || 0) + 1;
    return counts;
  }, [allItems]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allItems.filter((a) => {
      if (typeFilter !== "all" && a.type !== typeFilter) return false;
      if (!q) return true;
      return (
        (a.leadName || "").toLowerCase().includes(q) ||
        (a.agentName || "").toLowerCase().includes(q) ||
        (a.agentAgency || "").toLowerCase().includes(q) ||
        (a.description || "").toLowerCase().includes(q)
      );
    });
  }, [allItems, search, typeFilter]);

  // group by day (input is already newest-first)
  const grouped = useMemo(() => {
    const groups: { day: string; items: Activity[] }[] = [];
    for (const a of filtered.slice(0, visibleCount)) {
      const day = dayLabel(a.timestamp);
      const last = groups[groups.length - 1];
      if (last && last.day === day) last.items.push(a);
      else groups.push({ day, items: [a] });
    }
    return groups;
  }, [filtered, visibleCount]);

  const loadOlderFromServer = async () => {
    if (loadingOlder) return;
    setLoadingOlder(true);
    try {
      // cursor: oldest timestamp we currently hold
      const oldest = allItems.length ? allItems[allItems.length - 1].timestamp : undefined;
      const cursor = typeof nextBefore === "string" ? nextBefore : oldest;
      const res = await leadAssignmentService.getActivityFeed(cursor);
      setOlderItems((prev) => [...prev, ...res.items]);
      setNextBefore(res.nextBefore);
      setVisibleCount((c) => c + res.items.length);
    } catch {
      setNextBefore(null);
    } finally {
      setLoadingOlder(false);
    }
  };

  return (
    <Card className="flex flex-col h-[calc(100vh-220px)] min-h-[480px]">
      <CardHeader className="flex-shrink-0 space-y-2.5 pb-3">
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="text-lg font-semibold">
            Recent Activity
            <span className="ml-2 text-sm font-normal text-gray-400">{filtered.length}</span>
          </CardTitle>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setVisibleCount(30); }}
              placeholder="Search activity..."
              className="h-8 w-[180px] pl-8 text-xs"
            />
          </div>
        </div>
        {/* Type filter chips with live counts */}
        <div className="flex flex-wrap gap-1.5">
          {TYPE_CHIPS.filter((c) => c.key === "all" || (typeCounts[c.key] || 0) > 0).map((chip) => (
            <button
              key={chip.key}
              type="button"
              onClick={() => { setTypeFilter(chip.key); setVisibleCount(30); }}
              className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                typeFilter === chip.key
                  ? "bg-primary text-white border-primary"
                  : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
              }`}
            >
              {chip.label}
              <span className={`ml-1 tabular-nums ${typeFilter === chip.key ? "opacity-80" : "text-gray-400"}`}>
                {typeCounts[chip.key] || 0}
              </span>
            </button>
          ))}
        </div>
      </CardHeader>
      <CardContent className="flex-1 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <CheckCircle className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>{activities.length === 0 ? "No recent activity" : "Nothing matches your filters"}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {grouped.map((group) => (
              <div key={group.day}>
                <div className="sticky top-0 z-10 -mx-1 px-1 py-1 bg-white/95 backdrop-blur-sm">
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{group.day}</span>
                </div>
                <div className="space-y-3 mt-1.5">
                  {group.items.map((activity) => (
                    <div
                      key={activity.id}
                      className={`flex items-start gap-3 p-3 rounded-lg border ${getActivityColor(activity.type)}`}
                    >
                      <div className="flex-shrink-0 mt-1">{getActivityIcon(activity.type)}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="font-medium text-gray-900">{activity.leadName}</span>
                          {activity.householdPrimaryName && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200">
                              Household member of {activity.householdPrimaryName}
                            </span>
                          )}
                          <span className="text-gray-500">•</span>
                          <span className="text-sm text-gray-600">{activity.agentName}</span>
                          {activity.agentAgency && (
                            <span className="text-xs text-gray-400">· {activity.agentAgency}</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-700 mb-1">{activity.description}</p>
                        <p className="text-xs text-gray-500" title={new Date(activity.timestamp).toLocaleString()}>
                          {relativeTime(activity.timestamp)} · {formatTime(activity.timestamp)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <div className="pt-1 pb-2 text-center">
              {filtered.length > visibleCount ? (
                <button
                  type="button"
                  onClick={() => setVisibleCount((c) => c + 30)}
                  className="px-4 py-1.5 rounded-full text-xs font-medium border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
                >
                  Load more ({filtered.length - visibleCount} older)
                </button>
              ) : nextBefore !== null ? (
                <button
                  type="button"
                  disabled={loadingOlder}
                  onClick={loadOlderFromServer}
                  className="px-4 py-1.5 rounded-full text-xs font-medium border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  {loadingOlder ? "Loading older history..." : "Load older history"}
                </button>
              ) : (
                <span className="text-xs text-gray-400">End of history</span>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
